import { useContext, useState } from 'react';
import { format, parseISO } from 'date-fns';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { AnalyticsContext } from 'contexts/analytics';
import { SessionContext } from 'contexts/session';
import Notification from 'components/notification';
import {
  CHART_TITLE_MAP,
  ChartItemData,
  ChartSeriesConfig,
  CsvExportFailureReason,
  buildChartCsv,
  buildCsvFilename,
  downloadCsvBlob,
  getVisibleSeries,
  hasVisibleNonZeroData,
} from 'utils/chartCsvExport';
import {
  PngExportFailureReason,
  PngExportOptions,
  PngExportTeam,
  buildPngFilename,
  captureChartAsPng,
  downloadPngBlob,
} from 'utils/chartPngExport';
import { COLOR_CHART_STATUSES } from 'constants/analytics';
import { colors } from 'constants/styles/colors';
import ExportButton from '../ExportButton';

interface Props {
  /** Derived chart rows used to build the CSV / decide if the chart has data. */
  chartItems: ChartItemData[];
  /** Series configuration for the current chart (drives visible-series logic). */
  dataDisplay: ChartSeriesConfig[];
  /** Active chart type ('AreaChart' | 'BarChart'). */
  chartType: string;
  /** Optional chart title used as the PNG export heading. */
  title?: string;
  /** Ref to the chart container captured for the PNG export. */
  chartContainerRef: React.RefObject<HTMLDivElement>;
  /** When true (dashboard mini-chart) the export button is hidden. */
  isDashboardChart?: boolean;
}

const EXPORT_ERROR_MESSAGES: Record<CsvExportFailureReason, string> = {
  no_data: 'No chart data available to export.',
  data_point_limit_exceeded:
    'Export exceeds 10,000 data points. Narrow the date range or reduce visible series.',
  file_size_exceeded:
    'Generated CSV exceeds 10 MB. Narrow the date range or reduce visible series.',
  unexpected_chart_state: 'Unable to export: unexpected chart state.',
};

const PNG_EXPORT_ERROR_MESSAGES: Record<PngExportFailureReason, string> = {
  no_chart: 'No chart data available to export.',
  render_failed: 'Failed to render chart image. Please try again.',
  file_size_exceeded: 'Generated PNG exceeds 5 MB. Narrow the date range or reduce visible series.',
};

/**
 * Export-chart control rendered on the analytics chart header row, alongside the
 * metric tabs. Owns the PNG/CSV export lifecycle so the button can live in the
 * full-width header while the chart it captures lives in the chart column.
 */
const ChartExportButton = ({
  chartItems,
  dataDisplay,
  chartType,
  title,
  chartContainerRef,
  isDashboardChart,
}: Props): JSX.Element | null => {
  const [isExporting, setIsExporting] = useState(false);
  const { dashboardAnalyticsExports } = useFlags() || {};
  const { currentChart, filters } = useContext(AnalyticsContext);
  const { isAccountOwner } = useContext(SessionContext);

  const handlePngExport = async () => {
    if (isExporting) return;
    setIsExporting(true);
    await new Promise((resolve) => setTimeout(resolve, 0));
    try {
      if (!chartContainerRef.current) {
        Notification({ type: 'error', text: PNG_EXPORT_ERROR_MESSAGES.no_chart });
        return;
      }

      if (!hasVisibleNonZeroData(dataDisplay, chartType, chartItems)) {
        Notification({ type: 'error', text: PNG_EXPORT_ERROR_MESSAGES.no_chart });
        return;
      }

      const chartTitle = title?.trim() ? title : CHART_TITLE_MAP[currentChart] ?? currentChart;

      const dateRange =
        filters.date_from && filters.date_to
          ? `${format(parseISO(filters.date_from), 'MMM d, yyyy')} – ${format(
              parseISO(filters.date_to),
              'MMM d, yyyy'
            )}`
          : undefined;

      const visibleSeries = getVisibleSeries(dataDisplay, chartType).map((s) => {
        const colorKey = s.key.toUpperCase();
        const color =
          COLOR_CHART_STATUSES[colorKey]?.stroke ??
          COLOR_CHART_STATUSES[colorKey]?.fill ??
          colors.fonts.lightGray;
        return { label: s.label ?? s.key, color };
      });

      const fallbackLabel = isAccountOwner ? 'All users from the account' : 'All my teams';
      const teamFilter: PngExportTeam[] =
        filters.team_display?.length > 0
          ? filters.team_display.map(({ name, color }) => ({
              name,
              bgColor: color ? colors.dataVisualization[color]?.lighter : undefined,
            }))
          : [{ name: fallbackLabel }];

      const pngOpts: PngExportOptions = {
        title: chartTitle,
        dateRange,
        series: visibleSeries,
        teamFilter,
      };
      const result = await captureChartAsPng(chartContainerRef.current, pngOpts);
      if (result.ok === false) {
        Notification({ type: 'error', text: PNG_EXPORT_ERROR_MESSAGES[result.reason] });
        return;
      }
      downloadPngBlob(result.blob, buildPngFilename(currentChart));
    } catch {
      Notification({ type: 'error', text: 'Export failed due to an unexpected error.' });
    } finally {
      setIsExporting(false);
    }
  };

  const handleCsvExport = async () => {
    if (isExporting) return;
    setIsExporting(true);
    await new Promise((resolve) => setTimeout(resolve, 0));
    try {
      const result = buildChartCsv({
        chartTitle: CHART_TITLE_MAP[currentChart] ?? currentChart,
        chartDataType: currentChart,
        chartType,
        filters,
        dataDisplay,
        chartItems: chartItems ?? [],
      });

      if (result.ok === false) {
        Notification({ type: 'error', text: EXPORT_ERROR_MESSAGES[result.reason] });
        return;
      }

      downloadCsvBlob(result.csv, buildCsvFilename(currentChart));
    } catch {
      Notification({ type: 'error', text: 'Export failed due to an unexpected error.' });
    } finally {
      setIsExporting(false);
    }
  };

  if (!dashboardAnalyticsExports || isDashboardChart) {
    return null;
  }

  return (
    <ExportButton
      label="Export chart"
      loadingLabel="Exporting chart"
      isLoading={isExporting}
      disabled={false}
      data-testid="export-chart-button"
      items={[
        {
          key: 'png',
          label: 'Export as PNG',
          icon: 'ImageExport',
          iconColor: 'var(--black-dark)',
          onClick: handlePngExport,
          'data-testid': 'export-png-item',
        },
        {
          key: 'csv',
          label: 'Export as CSV',
          icon: 'TableExport',
          iconColor: 'var(--black-dark)',
          onClick: handleCsvExport,
          'data-testid': 'export-csv-item',
        },
      ]}
    />
  );
};

export default ChartExportButton;
